import {castDraft} from 'immer'

import type {AppDispatch, AppThunk} from '../../../actions/types'
import {overviewArg} from '../../../rest/projects'
import {
  generateSidebarItemId,
  getDefaultAllProjectsTree,
  getDefaultOverviewTree,
} from '../../../selectors/projectTrees'
import type {SetBuildTypeOrderArg} from '../../../services/rest'
import {restApi} from '../../../services/rest'
import {ROOT_PROJECT_ID} from '../../../types'
import type {BuildTypeId, ProjectId, ProjectInternalId} from '../../../types'
import {currentServerId} from '../../../types/projectTrees'
import type {ProjectsTreeItemBuildType, ProjectTreeItemProject} from '../../../types/projectTrees'
import asyncBatch, {asyncBatchById} from '../../../utils/asyncBatch'
import {notNull} from '../../../utils/guards'
import type {WritableKeyValue} from '../../../utils/object'

type ProjectsReorderArg = {
  dispatch: AppDispatch
  order: Array<ProjectInternalId>
}
const requestProjectsReorder = asyncBatch(({order, dispatch}: ProjectsReorderArg) =>
  dispatch(restApi.endpoints.setProjectOrder.initiate(order)),
)
const requestProjectsReorderAction =
  (order: ReadonlyArray<ProjectTreeItemProject>): AppThunk =>
  dispatch => {
    const patchResult = dispatch(
      restApi.util.updateQueryData('getAllProjectsNormalized', overviewArg, draft => {
        castDraft(draft).result = order.map(item => item.id)
      }),
    )
    requestProjectsReorder({dispatch, order: order.map(item => item.internalId)}).catch(
      patchResult.undo,
    )
  }
type BuildTypesReorderParams = SetBuildTypeOrderArg & {dispatch: AppDispatch}
const requestBuildTypesReorder = asyncBatchById({
  getId: ({projectId}) => projectId,
  asyncFn: ({dispatch, ...rest}: BuildTypesReorderParams) =>
    dispatch(restApi.endpoints.setBuildTypeOrder.initiate(rest)),
})

type BuildTypesReorderActionParams = {
  projectId: ProjectId
  order: ReadonlyArray<ProjectsTreeItemBuildType>
}
const requestBuildTypesReorderAction =
  ({projectId, order}: BuildTypesReorderActionParams): AppThunk =>
  dispatch => {
    const patchResult = dispatch(
      restApi.util.updateQueryData('getAllProjectsNormalized', overviewArg, draft => {
        const {buildTypes} = draft.entities.overviewProjects?.[projectId] ?? {}
        if (buildTypes != null) {
          buildTypes.buildType = order.map(item => item.id).filter(notNull)
        }
      }),
    )
    requestBuildTypesReorder({
      projectId,
      order: order.map(item => item.internalId ?? ''),
      dispatch,
    }).catch(patchResult?.undo)
  }
export const moveProject =
  (projectId: ProjectId, shiftBy: number): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const overviewTree = getDefaultOverviewTree(state)

    if (overviewTree == null) {
      return
    }

    const currentItem = overviewTree?.hash?.get(
      generateSidebarItemId(currentServerId, 'project', projectId),
    )

    if (currentItem == null) {
      return
    }

    const siblings = overviewTree.data.filter(
      item => item.itemType === 'project' && item.parentId === currentItem.parentId,
    )
    const currentSiblingIndex = siblings.indexOf(currentItem)
    const subtreeStart = overviewTree.data.indexOf(currentItem)
    const subtreeEnd =
      overviewTree.data.findIndex(
        (item, i) => i > subtreeStart && item.level <= currentItem.level,
      ) ?? overviewTree.data.length
    let newSiblingIndex = currentSiblingIndex + shiftBy

    if (newSiblingIndex < 0) {
      newSiblingIndex = 0
    } else if (newSiblingIndex >= siblings.length) {
      newSiblingIndex = siblings.length - 1
    }

    if (newSiblingIndex === currentSiblingIndex) {
      return
    }

    const siblingToInsertBefore = siblings[shiftBy > 0 ? newSiblingIndex + 1 : newSiblingIndex]
    const insertIndex =
      siblingToInsertBefore != null
        ? overviewTree.data.indexOf(siblingToInsertBefore)
        : overviewTree.data.length
    const newData =
      shiftBy > 0
        ? [
            ...overviewTree.data.slice(0, subtreeStart),
            ...overviewTree.data.slice(subtreeEnd, insertIndex),
            ...overviewTree.data.slice(subtreeStart, subtreeEnd),
            ...overviewTree.data.slice(insertIndex),
          ]
        : [
            ...overviewTree.data.slice(0, insertIndex),
            ...overviewTree.data.slice(subtreeStart, subtreeEnd),
            ...overviewTree.data.slice(insertIndex, subtreeStart),
            ...overviewTree.data.slice(subtreeEnd),
          ]
    const orderedProjects: Array<ProjectTreeItemProject> = []
    // can't use filter, see https://github.com/facebook/flow/issues/1414
    newData.forEach(item => {
      if (item.itemType === 'project') {
        orderedProjects.push(item)
      }
    })
    dispatch(requestProjectsReorderAction(orderedProjects))
  }
export const moveBuildType =
  (buildTypeId: BuildTypeId, shiftBy: number): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const overviewTree = getDefaultOverviewTree(state)

    if (overviewTree == null) {
      return
    }

    const currentItem = overviewTree?.hash?.get(
      generateSidebarItemId(currentServerId, 'buildType', buildTypeId),
    )

    if (currentItem == null) {
      return
    }

    const projectId = currentItem.parentId

    if (projectId == null) {
      return
    }

    const siblings = overviewTree.data.filter(
      item => item.itemType === 'buildType' && item.parentId === projectId,
    )
    const currentSiblingIndex = siblings.indexOf(currentItem)
    let newSiblingIndex = currentSiblingIndex + shiftBy

    if (newSiblingIndex < 0) {
      newSiblingIndex = 0
    } else if (newSiblingIndex >= siblings.length) {
      newSiblingIndex = siblings.length - 1
    }

    if (newSiblingIndex === currentSiblingIndex) {
      return
    }

    const newData =
      shiftBy > 0
        ? [
            ...siblings.slice(0, currentSiblingIndex),
            ...siblings.slice(currentSiblingIndex + 1, newSiblingIndex + 1),
            currentItem,
            ...siblings.slice(newSiblingIndex + 1),
          ]
        : [
            ...siblings.slice(0, newSiblingIndex),
            currentItem,
            ...siblings.slice(newSiblingIndex, currentSiblingIndex),
            ...siblings.slice(currentSiblingIndex + 1),
          ]
    const orderedBuildTypes: Array<ProjectsTreeItemBuildType> = []
    // can't use filter, see https://github.com/facebook/flow/issues/1414
    newData.forEach(item => {
      if (item.itemType === 'buildType') {
        orderedBuildTypes.push(item)
      }
    })
    dispatch(
      requestBuildTypesReorderAction({
        projectId,
        order: orderedBuildTypes,
      }),
    )
  }
export const resetOrderInProject =
  (projectId: ProjectId): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const overviewTree = getDefaultOverviewTree(state)
    const allProjectsTree = getDefaultAllProjectsTree(state)

    if (overviewTree == null || allProjectsTree == null) {
      return
    }

    const itemId = generateSidebarItemId(currentServerId, 'project', projectId)
    const currentItem = overviewTree?.hash?.get(itemId)
    const currentAllProjectsItem = allProjectsTree?.hash?.get(itemId)

    if (currentItem == null || currentAllProjectsItem == null) {
      return
    }

    const subtreeStart = overviewTree.data.indexOf(currentItem)
    const subtreeEnd =
      overviewTree.data.findIndex(
        (item, i) => i > subtreeStart && item.level <= currentItem.level,
      ) ?? overviewTree.data.length
    const descendants = overviewTree.data.slice(subtreeStart + 1, subtreeEnd)
    const projectChildrenRanges: WritableKeyValue<
      ProjectId,
      ReadonlyArray<ProjectTreeItemProject>
    > = {}
    let currentRange: Array<ProjectTreeItemProject> | null | undefined = null

    for (const item of descendants) {
      if (item.itemType !== 'project') {
        continue
      }

      if (item.parentId === projectId) {
        currentRange = [item]
        projectChildrenRanges[item.id] = currentRange
      } else {
        currentRange?.push(item)
      }
    }

    const unorderedChildren =
      currentAllProjectsItem.childrenProjectsIds?.flatMap(id => projectChildrenRanges[id] ?? []) ??
      []
    const newData = [
      ...overviewTree.data.slice(0, subtreeStart + 1),
      ...unorderedChildren,
      ...overviewTree.data.slice(subtreeEnd),
    ]
    const projects: Array<ProjectTreeItemProject> = []
    // can't use filter, see https://github.com/facebook/flow/issues/1414
    newData.forEach(item => {
      if (item.itemType === 'project') {
        projects.push(item)
      }
    })
    dispatch(requestProjectsReorderAction(projects))
    dispatch(
      requestBuildTypesReorderAction({
        projectId,
        order:
          currentAllProjectsItem.childrenBuildTypesIds
            ?.map(id =>
              overviewTree.data.find((item): item is ProjectsTreeItemBuildType => item.id === id),
            )
            .filter(notNull) ?? [],
      }),
    )
  }
export const resetOrderInRootProject = (): AppThunk => (dispatch, getState) => {
  const state = getState()
  const overviewTree = getDefaultOverviewTree(state)
  const allProjectsTree = getDefaultAllProjectsTree(state)

  if (overviewTree == null || allProjectsTree == null) {
    return
  }

  const projectChildrenRanges: WritableKeyValue<
    ProjectId,
    ReadonlyArray<ProjectTreeItemProject>
  > = {}
  let currentRange: Array<ProjectTreeItemProject> | null | undefined = null

  for (const item of overviewTree.data) {
    if (item.itemType !== 'project') {
      continue
    }

    if (item.parentId === ROOT_PROJECT_ID) {
      currentRange = [item]
      projectChildrenRanges[item.id] = currentRange
    } else {
      currentRange?.push(item)
    }
  }

  const unorderedChildren =
    allProjectsTree.data.flatMap(item =>
      item.itemType === 'project' && item.parentId === ROOT_PROJECT_ID
        ? projectChildrenRanges[item.id] ?? []
        : [],
    ) ?? []
  const projects: Array<ProjectTreeItemProject> = []
  // can't use filter, see https://github.com/facebook/flow/issues/1414
  unorderedChildren.forEach(item => {
    if (item.itemType === 'project') {
      projects.push(item)
    }
  })
  dispatch(requestProjectsReorderAction(projects))
}
